import { useMemo, useState } from 'react'
import { HorizontalScroller } from '@/components/commons/horizontal-scroller'
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '@/components/ui/combobox'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { CheckIcon, PlusIcon } from '@/components/ui/icons'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import { ComboboxTrigger } from '@/features/marquee/components/combobox-trigger'
import { CtaBadge } from '@/features/marquee/components/cta-badge'
import { SearchInput } from '@/features/marquee/components/search-input'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { cn } from '@/helpers/utils'

interface AssignCtaControlProps {
	id: string
	/** The event's full CTA library, library order. */
	options: ComboboxOption[]
	/** Already-pitched CTAs, so the mobile list can check them off in place. */
	pitched: ComboboxOption[]
	onSelect: (option: ComboboxOption) => void
	onDeselect: (option: ComboboxOption) => void
	placeholder: string
	searchPlaceholder: string
	searchClearLabel: string
	emptyLabel: string
	removeLabel: (label: string) => string
}

/**
 * Adds and removes CTAs for this attendee. Desktop is a searchable multi-select
 * over the whole library, the pitched ones ticked: capture is a handful of CTAs
 * per attendee, and a single-select closed the popup on every pick, so building
 * a set of four meant opening the same list four times. Multi-select keeps it
 * open until the operator is done, and lets a mistake be un-ticked in place
 * rather than removed from the pill row afterwards. Below the
 * `l` breakpoint that control is a two-handed, keyboard-driven popup over a
 * 39-item list (OPSSTD-5132), so mobile opens a bottom drawer instead: the
 * full library as plain tappable rows, a checkmark on the ones already
 * pitched, a search field to narrow the list, and those same pills mirrored
 * at the top (horizontally scrollable, so a long selection never crowds out
 * the list) so a selection is visible without closing the sheet. The drawer
 * has no explicit close action by design — every tap is a save, so there's
 * nothing to confirm; dismiss it by dragging down or tapping outside.
 */
export function AssignCtaControl({
	id,
	options,
	pitched,
	onSelect,
	onDeselect,
	placeholder,
	searchPlaceholder,
	searchClearLabel,
	emptyLabel,
	removeLabel
}: AssignCtaControlProps) {
	const isDesktop = useMinWidth('l')
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')

	const pitchedSet = useMemo(
		() => new Set(pitched.map((option) => option.value)),
		[pitched]
	)

	if (isDesktop) {
		// base-ui matches option objects referentially, so the selection is
		// derived from `options` rather than from the `pitched` copies, which
		// carry the same codes as different objects.
		const selectedOptions = options.filter((option) =>
			pitchedSet.has(option.value)
		)
		return (
			<Combobox
				items={options}
				multiple
				onValueChange={(next: ComboboxOption[]) => {
					// One press toggles one option, so the diff is a single
					// add or a single remove. Reported as such rather than as
					// the whole next set, because each change is its own save:
					// the caller decides where an addition lands in the order.
					const nextValues = new Set(next.map((o) => o.value))
					const added = next.find(
						(option) => !pitchedSet.has(option.value)
					)
					if (added) {
						onSelect(added)
						return
					}
					const removed = selectedOptions.find(
						(option) => !nextValues.has(option.value)
					)
					if (removed) {
						onDeselect(removed)
					}
				}}
				value={selectedOptions}
			>
				<ComboboxTrigger id={id}>
					<span className='truncate text-neutral-400'>
						{placeholder}
					</span>
				</ComboboxTrigger>
				<ComboboxContent className='w-(--anchor-width)'>
					<ComboboxInput placeholder={searchPlaceholder} />
					<ComboboxList>
						{(item: ComboboxOption) => (
							<ComboboxItem
								className='whitespace-nowrap'
								key={item.value}
								value={item}
							>
								{item.label}
							</ComboboxItem>
						)}
					</ComboboxList>
					<ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>
		)
	}

	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(query.trim().toLowerCase())
	)

	return (
		<Drawer
			onOpenChange={(next) => {
				setOpen(next)
				if (!next) {
					setQuery('')
				}
			}}
			open={open}
		>
			<DrawerTrigger asChild>
				<button
					className='flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3.5 text-base text-neutral-400'
					id={id}
					type='button'
				>
					{placeholder}
					<PlusIcon className='size-4 shrink-0 text-neutral-500' />
				</button>
			</DrawerTrigger>
			<DrawerContent>
				<DrawerHeader className='pb-2'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{placeholder}
					</DrawerTitle>
				</DrawerHeader>
				{pitched.length > 0 && (
					<HorizontalScroller className='pb-3'>
						<div className='flex w-max gap-2 px-4'>
							{pitched.map((option) => (
								<CtaBadge
									className='shrink-0'
									key={option.value}
									label={option.label}
									onRemove={() => onDeselect(option)}
									removeLabel={removeLabel(option.label)}
								/>
							))}
						</div>
					</HorizontalScroller>
				)}
				<div className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 pb-4'>
					{filteredOptions.length === 0 ? (
						<p className='py-6 text-center text-neutral-500 text-sm'>
							{emptyLabel}
						</p>
					) : (
						filteredOptions.map((option) => {
							const isPitched = pitchedSet.has(option.value)
							return (
								<button
									className={cn(
										'flex w-full items-center justify-between gap-2 rounded-lg border px-4 py-3 text-left text-sm active:bg-grey-100',
										isPitched
											? 'border-neutral-900 text-neutral-900'
											: 'border-neutral-200 text-neutral-900'
									)}
									key={option.value}
									onClick={() =>
										isPitched
											? onDeselect(option)
											: onSelect(option)
									}
									type='button'
								>
									{option.label}
									{isPitched && (
										<CheckIcon className='size-4 shrink-0' />
									)}
								</button>
							)
						})
					)}
				</div>
				<DrawerFooter>
					<SearchInput
						clearLabel={searchClearLabel}
						onChange={setQuery}
						placeholder={searchPlaceholder}
						value={query}
					/>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
